import { useState } from "react";
import { signIn } from "../auth/client";
import { loginSchema, type LoginInput } from "../lib/validations";
import { BrandLogo, companyName } from "../components/BrandLogo";

interface LoginProps {
  onSuccess: () => void;
  onRegisterClick: () => void;
}

type FieldErrors = Partial<Record<keyof LoginInput, string>>;

// Demo account credentials
const DEMO_EMAIL = "demo@salesgrid.io";
const DEMO_PASSWORD = "demo1234";

export function Login({ onSuccess, onRegisterClick }: LoginProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<FieldErrors>({});
  const [serverError, setServerError] = useState("");
  const [loading, setLoading] = useState(false);

  const validateField = (field: keyof LoginInput, value: string) => {
    const partialData = { email, password, [field]: value };
    const result = loginSchema.safeParse(partialData);

    if (!result.success) {
      const flattened = result.error.flatten();
      const fieldErrors = flattened.fieldErrors[field];
      setErrors((prev) => ({
        ...prev,
        [field]: fieldErrors?.[0],
      }));
    } else {
      setErrors((prev) => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError("");

    // Validate all fields
    const result = loginSchema.safeParse({ email, password });

    if (!result.success) {
      const flattened = result.error.flatten();
      const fieldErrors: FieldErrors = {};
      for (const [field, messages] of Object.entries(flattened.fieldErrors)) {
        if (messages && messages.length > 0) {
          fieldErrors[field as keyof LoginInput] = messages[0];
        }
      }
      setErrors(fieldErrors);
      return;
    }

    setLoading(true);

    try {
      const signInResult = await signIn.email({
        email: result.data.email,
        password: result.data.password,
      });

      if (signInResult.error) {
        setServerError(signInResult.error.message || "Failed to sign in");
      } else {
        onSuccess();
      }
    } catch (err) {
      setServerError("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-[var(--brand-gradient-start)]/20 to-[var(--brand-gradient-end)]/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-[var(--brand-gradient-end)]/20 to-[var(--brand-gradient-start)]/20 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-md relative">
        {/* Logo */}
        <div className="flex items-center justify-center mb-8">
          <BrandLogo variant="primary" className="h-12 w-auto" />
        </div>

        {/* Login Card */}
        <div className="bg-card rounded-2xl p-5 sm:p-8 shadow-brand-lg border border-border">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold mb-2">Welcome to {companyName}</h1>
            <p className="text-muted-foreground">Sign in to access the dashboard</p>
          </div>

          {/* Demo Account Box */}
          <div className="mb-6 p-4 rounded-xl bg-[var(--brand-primary)]/10 border border-[var(--brand-primary)]/20">
            <p className="text-sm font-medium text-[var(--brand-primary)] mb-2">Demo Account</p>
            <div className="space-y-1 text-sm">
              <p className="text-foreground">
                <span className="text-muted-foreground">Email:</span> {DEMO_EMAIL}
              </p>
              <p className="text-foreground">
                <span className="text-muted-foreground">Password:</span> {DEMO_PASSWORD}
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setEmail(DEMO_EMAIL);
                setPassword(DEMO_PASSWORD);
              }}
              className="mt-3 w-full py-2 rounded-lg bg-[var(--brand-primary)]/20 text-[var(--brand-primary)] text-sm font-medium hover:bg-[var(--brand-primary)]/30 transition-colors"
            >
              Use Demo Credentials
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {serverError && (
              <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-600 text-sm">
                {serverError}
              </div>
            )}

            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) validateField("email", e.target.value);
                }}
                onBlur={(e) => validateField("email", e.target.value)}
                placeholder="you@example.com"
                className={`w-full px-4 py-3 rounded-xl bg-secondary border-0 text-sm focus:outline-none focus:ring-2 transition-all placeholder:text-muted-foreground ${
                  errors.email
                    ? "ring-2 ring-rose-500/30 focus:ring-rose-500/30"
                    : "focus:ring-[var(--brand-primary)]/30"
                }`}
              />
              {errors.email && (
                <p className="text-sm text-rose-500 mt-1">{errors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errors.password) validateField("password", e.target.value);
                }}
                onBlur={(e) => validateField("password", e.target.value)}
                placeholder="Enter your password"
                className={`w-full px-4 py-3 rounded-xl bg-secondary border-0 text-sm focus:outline-none focus:ring-2 transition-all placeholder:text-muted-foreground ${
                  errors.password
                    ? "ring-2 ring-rose-500/30 focus:ring-rose-500/30"
                    : "focus:ring-[var(--brand-primary)]/30"
                }`}
              />
              {errors.password && (
                <p className="text-sm text-rose-500 mt-1">{errors.password}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)] text-white font-semibold text-sm hover:shadow-lg hover:shadow-[var(--brand-primary)]/25 transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Signing in...
                </span>
              ) : (
                "Sign in"
              )}
            </button>
          </form>

        </div>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground mt-6">
          {companyName} Dashboard Demo
        </p>
      </div>
    </div>
  );
}
